<script setup lang="ts">
import { AccountProtectionAccordion } from '@/types/UserLimits';

const { t } = useTexts();
const route = useRoute();
const { goToLocaleRoute, getSecureQueryParam } = useRoutes();

const { openDepositModal, openWithdrawModal } = usePayments();
const { logout: logoutFromStore } = useAuthStore();
const { runAction } = useSmarticoInit();

const { isNotificationActived } = storeToRefs(useSmarticoConfigs());
const { unReadMessages } = storeToRefs(useSmarticoMessages());
const { currentMenuOpen } = storeToRefs(useAccountMenuStore());
const { setCurrentMenuOpen } = useAccountMenuStore();
const { userCanOnlyWithdraw } = useUserLimits();
const { isPokerEnabled, isIrpfEnabled } = useFeatureFlagsEnv();
const { isUserRefersEnabled } = useFeatureFlagsJson();

const { isUserCompleteRegistration, hasAnyContractPending } =
  useUserValidations();

const { userHasStrongPassword } = useUserData();

const { isContractsSectionEnabled } = useFeatureFlagsNew();

const { forceOpenAccordion } = useAccountProtection();

const { walletIsLoading, walletHasReal } = useWallet();

const { realWallet } = storeToRefs(useWalletStore());

const isWithdrawEnabledNow = computed(() => {
  return realWallet.value.withdraw_enable_now;
});

const isWithdrawEnabledForUser = computed(() => {
  return !!realWallet.value.withdrawEnabled;
});

const isWithdrawDisabled = computed(() => {
  return (
    walletIsLoading.value ||
    !isWithdrawEnabledNow.value ||
    !isWithdrawEnabledForUser.value ||
    !walletHasReal.value
  );
});

const unReadMessagesCount = computed(() => {
  return unReadMessages.value >= 9 ? '9+' : unReadMessages.value;
});

const isPendingProfile = computed<boolean>(() => {
  if (isContractsSectionEnabled.value) {
    return !isUserCompleteRegistration.value || hasAnyContractPending.value;
  }

  return !isUserCompleteRegistration.value;
});

const allMenuItems = computed<AccountMenu.Item[]>(() => {
  return [
    {
      icon: 'user/user_circle',
      text: t('account_menu.my_profile'),
      group: 'user-account',
      condition: true,
      children: [
        {
          icon: 'user/user_circle',
          text: t('account_menu.account_data'),
          routeName: 'user-account',
          condition: true,
          pill: isPendingProfile.value ? '!' : 'icon-check',
          pillClass: isPendingProfile.value ? 'w-auto px-1.5' : 'w-auto px-1',
        },
        {
          icon: 'user/password',
          text: t('account_menu.security'),
          routeName: 'user-security',
          condition: true,
          pill: !userHasStrongPassword.value ? '!' : 'icon-check',
          pillClass: !userHasStrongPassword.value
            ? 'w-auto px-1.5'
            : 'w-auto px-1',
        },
        {
          icon: 'user/history',
          text: t('account_menu.login_history'),
          routeName: 'user-login-history',
          condition: true,
        },
        {
          icon: 'user/protection',
          text: t('account_menu.responsible_gaming'),
          condition: true,
          children: [
            {
              icon: 'user/account_limits',
              text: t('account_menu.account_limits'),
              action: '#account-protection-limits',
              activeWhen: { routeName: 'user-account-protection' },
              condition: true,
            },
            {
              icon: 'user/timeout_limits',
              text: t('account_menu.timeout_limits'),
              action: '#account-protection-timeout',
              activeWhen: {
                routeName: 'user-account-protection',
                routeQuery: { section: 'timeout' },
              },
              condition: true,
            },
            {
              icon: 'user/self_exclusion',
              text: t('account_menu.self_exclusion'),
              action: '#account-protection-exclusion',
              activeWhen: {
                routeName: 'user-account-protection',
                routeQuery: { section: 'exclusion' },
              },
              condition: true,
            },
          ],
        },
      ],
    },
    {
      icon: 'user/balance_management',
      text: t('account_menu.balance_management'),
      group: 'user-wallet',
      condition: true,
      children: [
        {
          icon: 'user/wallet',
          text: t('account_menu.wallet'),
          routeName: 'user-wallet',
          routeQuery: {
            select: 'user-wallet',
          },
          condition: true,
        },
        {
          icon: 'user/deposit',
          text: t('account_menu.deposit'),
          action: '#deposit',
          condition: !userCanOnlyWithdraw.value,
        },
        {
          icon: 'user/withdrawal',
          text: t('account_menu.withdrawal'),
          action: isWithdrawDisabled.value ? '#' : '#withdraw',
          condition: true,
          customClass: isWithdrawDisabled.value ? 'isDisabled' : 'isNormal',
        },
        {
          icon: 'user/history',
          text: t('account_menu.transactions_history'),
          routeName: 'user-wallet-transactions',
          condition: true,
        },
        {
          icon: 'user/withdrawal_status',
          text: t('account_menu.withdrawal_status'),
          customClass: 'isAlmostHidden',
          pill: t('account_menu.soon'),
          pillClass: 'w-auto bg-warning px-1.5',
          // routeName: 'user-wallet-last-withdraw',
          condition: false,
        },
      ],
    },
    {
      icon: 'user/history',
      text: t('account_menu.bets_history'),
      group: 'user-history',
      condition: true,
      children: [
        {
          icon: 'joystick',
          text: t('account_menu.casino_bets'),
          routeName: 'user-history-casino',
          condition: true,
        },
        {
          icon: 'futbol',
          text: t('account_menu.sports_bets'),
          routeName: 'user-history-sports',
          condition: true,
        },
        {
          icon: 'poker',
          text: t('account_menu.poker_bets'),
          customClass: 'isAlmostHidden',
          pill: t('account_menu.soon'),
          pillClass: 'w-auto bg-warning px-1.5',
          condition: isPokerEnabled.value,
          // routeName: 'user-history-poker',
        },
      ],
    },
    {
      icon: 'user/extract',
      text: t('account_menu.extract'),
      routeName: 'user-history-full',
      condition: true,
    },
    {
      icon: 'user/notification',
      text: t('account_menu.notifications'),
      action: '#openGamificationNotifications',
      condition: !!isNotificationActived.value && !userCanOnlyWithdraw.value,
      pill: unReadMessagesCount.value,
    },
    {
      icon: 'user/refers',
      text: t('account_menu.referral_bonus'),
      routeName: 'user-refers',
      condition: isUserRefersEnabled.value,
    },
    {
      icon: 'user/rewards',
      text: t('account_menu.rewards'),
      routeName: 'user-rewards',
      condition: true,
    },
    {
      icon: 'document',
      text: t('income_report.title'),
      routeName: 'user-irpf',
      condition: isIrpfEnabled.value,
    },
    {
      icon: 'document',
      text: t('income_report_external.menu_title', {
        year: String(new Date().getFullYear() - 1),
      }),
      routeName: 'user-irpf-rendimentos',
      condition: true,
    },
    {
      icon: 'user/logout',
      text: t('account_menu.logout'),
      action: '#logout',
      condition: true,
      customClass: 'isOpaque',
    },
  ];
});

const menuItems = computed(() => {
  return allMenuItems.value.filter((item) => item.condition);
});

const currentMenuOpenTitle = computed(() => {
  if (!currentMenuOpen.value) {
    return '';
  }

  const findItem = menuItems.value.find(
    (item) => item.group === currentMenuOpen.value,
  );

  return findItem?.text || '';
});

const submenuItems = computed(() => {
  if (!currentMenuOpen.value) {
    return [];
  }

  const findItem = menuItems.value.find(
    (item) => item.group === currentMenuOpen.value,
  );

  if (!findItem || !findItem?.children) {
    return [];
  }

  return findItem?.children.filter((item) => item.condition);
});

const expandedAccordionItem = ref<string | null>(null);

function toggleAccordion(itemText: string) {
  expandedAccordionItem.value =
    expandedAccordionItem.value === itemText ? null : itemText;
}

function isAccordionActive(item: AccountMenu.Item): boolean {
  if (!item.children) return false;

  return item.children.some((child) => {
    if (!child.activeWhen) return false;
    if (route.name !== child.activeWhen.routeName) return false;

    const q = child.activeWhen.routeQuery as Record<string, string> | undefined;

    if (q && Object.keys(q).length > 0) {
      return Object.entries(q).every(([key, val]) => route.query[key] === val);
    }

    return !route.query.section;
  });
}

const handleMenuItemClick = async (item: AccountMenu.Item) => {
  if (item.children) {
    setCurrentMenuOpen(item.group);
  } else if (item.action) {
    if (item.action === '#deposit') {
      openDepositModal();
    } else if (item.action === '#withdraw') {
      openWithdrawModal();
    } else if (item.action === '#logout') {
      await logoutFromStore({
        displayMessage: true,
        redirectToHome: true,
        origin: 'user',
      });
    } else if (item.action === '#openGamificationNotifications') {
      openGamificationMenu();
    } else if (item.action === '#account-protection-limits') {
      goToLocaleRoute({ name: 'user-account-protection' });
    } else if (item.action === '#account-protection-timeout') {
      forceOpenAccordion(AccountProtectionAccordion.Timeout);
      goToLocaleRoute({
        name: 'user-account-protection',
        query: { section: 'timeout' },
      });
    } else if (item.action === '#account-protection-exclusion') {
      forceOpenAccordion(AccountProtectionAccordion.Exclusion);
      goToLocaleRoute({
        name: 'user-account-protection',
        query: { section: 'exclusion' },
      });
    }
  } else if (item.routeName) {
    goToLocaleRoute({ name: item.routeName });
  }
};

function openGamificationMenu() {
  runAction('dp:inbox');
  setCurrentMenuOpen(undefined);
}

function closeSubmenu() {
  setCurrentMenuOpen(undefined);
  expandedAccordionItem.value = null;
}

watch(
  () => route.query,
  (query) => {
    if (!query || !query.select) {
      return;
    }

    const querySelect = getSecureQueryParam(query.select);

    const item = menuItems.value.find(
      (menuItem) => menuItem.group === querySelect,
    );

    if (item) {
      setCurrentMenuOpen(item.group);
    }
  },
  { immediate: true },
);

watch(
  () => route.fullPath,
  () => {
    const userAccountGroup = menuItems.value.find(
      (item) => item.group === 'user-account',
    );

    const accordionItem = userAccountGroup?.children?.find(
      (child) => child.children && isAccordionActive(child),
    );

    if (accordionItem) {
      expandedAccordionItem.value = accordionItem.text;
    }
  },
  { immediate: true },
);
</script>

<template>
  <div :class="$style.accountMenu">
    <div
      v-if="!currentMenuOpen"
      :class="$style.accountMenuItems"
    >
      <template
        v-for="item in menuItems"
        :key="item.text"
      >
        <UserMenuItem
          :item="item"
          @handle-menu-item-click="handleMenuItemClick"
        />
      </template>
    </div>
    <div
      v-else
      :class="$style.accountMenuItems"
    >
      <div
        :class="$style.submenuTitle"
        @click="closeSubmenu"
      >
        <NuxtIcon
          :class="$style.itemIcon"
          name="arrows/angle_left"
        />
        <span :class="$style.itemText">{{ currentMenuOpenTitle }}</span>
      </div>
      <template
        v-for="item in submenuItems"
        :key="item.text"
      >
        <template v-if="item.children">
          <button
            :class="[
              $style.accordionHeader,
              isAccordionActive(item) || expandedAccordionItem === item.text
                ? $style.accordionHeaderActive
                : '',
            ]"
            @click="toggleAccordion(item.text)"
          >
            <NuxtIcon
              :class="$style.itemIcon"
              :name="item.icon"
            />
            <span :class="$style.itemText">{{ item.text }}</span>
            <NuxtIcon
              :class="[
                $style.itemArrow,
                expandedAccordionItem === item.text ? $style.itemArrowOpen : '',
              ]"
              name="arrows/angle_right"
            />
          </button>
          <Transition name="accordion-slide">
            <div
              v-if="expandedAccordionItem === item.text"
              :class="$style.accordionContent"
            >
              <UserMenuItem
                v-for="child in item.children.filter((c) => c.condition)"
                :key="child.text"
                :item="child"
                @handle-menu-item-click="handleMenuItemClick"
              />
            </div>
          </Transition>
        </template>
        <UserMenuItem
          v-else
          :item="item"
          @handle-menu-item-click="handleMenuItemClick"
        />
      </template>
    </div>
  </div>
</template>

<style module lang="postcss">
.accountMenu {
  @apply w-full min-w-[300px];

  .accountMenuItems {
    @apply flex flex-col gap-2 items-center justify-start;
  }

  .accordionHeader {
    @apply w-full text-texts/90 rounded-md flex items-center py-3.5 px-4 gap-3 relative cursor-pointer;

    background: rgba(0, 201, 255, 0.1);

    .itemIcon {
      @apply text-lg;
      color: #fff;
    }

    .itemText {
      @apply uppercase font-semibold text-xs;
    }

    .itemArrow {
      @apply ml-auto transition-transform duration-200;
      color: #fff;
    }

    .itemArrowOpen {
      transform: rotate(90deg);
    }
  }

  .accordionHeaderActive {
    border: 1px solid #00c9ff;
    background: rgba(0, 201, 255, 0.1);
    backdrop-filter: blur(10px);

    .itemIcon {
      color: #00c9ff;
    }

    .itemText {
      color: #fff;
    }

    .itemArrow {
      color: #00c9ff;
    }
  }

  .accordionContent {
    @apply w-full flex flex-col gap-2;
  }

  .submenuTitle {
    @apply w-full text-texts/90 rounded-[10px] flex items-center py-3.5 px-4 gap-3 relative text-texts cursor-pointer;

    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    overflow: hidden;
    isolation: isolate;

    &:hover {
      @apply opacity-90;
    }

    .itemIcon {
      @apply text-lg opacity-70 text-primary;
    }

    .itemText {
      @apply uppercase text-xs font-bold;
    }
  }
}
</style>

<style>
.accordion-slide-enter-active,
.accordion-slide-leave-active {
  display: grid;
  transition:
    grid-template-rows 0.25s ease,
    opacity 0.25s ease;
}

.accordion-slide-enter-from,
.accordion-slide-leave-to {
  grid-template-rows: 0fr;
  opacity: 0;
}

.accordion-slide-enter-to,
.accordion-slide-leave-from {
  grid-template-rows: 1fr;
  opacity: 1;
}

.accordion-slide-enter-active > *,
.accordion-slide-leave-active > * {
  overflow: hidden;
}
</style>
